import clsx from 'clsx'
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react'
import type { ReactNode, ThHTMLAttributes } from 'react'
import type { SortDirection } from '../../lib/useSortState'

export interface SortableThProps<K extends string = string>
  extends Omit<ThHTMLAttributes<HTMLTableCellElement>, 'children'> {
  sortKey: K
  activeKey: K | null
  direction: SortDirection
  onSort: (key: K) => void
  children: ReactNode
}

export function SortableTh<K extends string = string>({
  sortKey,
  activeKey,
  direction,
  onSort,
  children,
  className,
  scope = 'col',
  ...props
}: SortableThProps<K>) {
  const active = activeKey === sortKey

  return (
    <th
      scope={scope}
      className={clsx(
        'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400',
        className
      )}
      {...props}
    >
      <button
        type="button"
        className={clsx(
          '-mx-1 flex w-full items-center gap-1 rounded px-1 py-0.5 text-left transition-colors',
          'hover:bg-gray-100 dark:hover:bg-gray-800',
          active && 'text-gray-900 dark:text-gray-100'
        )}
        onClick={() => onSort(sortKey)}
        aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <span className="flex-1">{children}</span>
        <span className="inline-flex shrink-0 text-gray-500 dark:text-gray-400" aria-hidden>
          {active ? (
            direction === 'asc' ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )
          ) : (
            <ChevronsUpDown className="h-4 w-4 opacity-45" />
          )}
        </span>
      </button>
    </th>
  )
}
